import React, { useState, useRef } from "react";
import {
  Form,
  Input,
  InputNumber,
  message,
  Pagination,
  Table,
  Typography,
} from "antd";
import { updateDietApi } from "../../../services/diet.services";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";
import "./DietMenu.css";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <InputNumber className={inputStyles.mdNumberInput} step={1} min={0} />
    ) : (
      <Input className={inputStyles?.mdInput} style={{ width: "100%" }} />
    );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={
            dataIndex === "name"
              ? [
                  {
                    required: true,
                    message: `Please Input ${title}!`,
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === "") {
                        return Promise.reject(
                          new Error(`Please provide diet name`)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    max: 100,
                    message: "Ensure this field has no more than 100 characters.",
                  },
                ]
              : []
          }
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};
const DietTableEdit = ({
  loading,
  data,
  setData,
  page,
  setPage,
  refreshPage,
  userDetails,
}) => {
  const [form] = Form.useForm();
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    data?.data?.length ?? 0
  );

  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record) => record?.id === editingKey?.id;

  const edit = (record) => {
    form.setFieldsValue({
      name: "",
      protien: "",
      calories: "",
      ...record,
    });
    setEditingKey(record);
  };
  const cancel = () => {
    setEditingKey("");
  };
  const save = async (record) => {
    try {
      const updatedValues = await form.validateFields();
      const newData = [...data?.data];
      const index = newData.findIndex((item) => record.id === item.id);
      if (index > -1) {
        const item = newData[index];
        const res = await updateDietApi(record?.id, updatedValues);
        newData.splice(index, 1, { ...item, ...updatedValues });
        setData({
          ...data,
          data: newData,
        });
        refreshPage();
        message.success(res?.data?.message || "Diet item updated successfully");
        setEditingKey("");
      } else {
        message.error("Row not found");
      }
    } catch (err) {
      const backendNameError = err?.response?.data?.message?.name?.[0];
      if (backendNameError) {
        message.error(backendNameError);
      }
      console.error(backendNameError || err);
    }
  };
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const columns = [
    {
      title: "Diet code",
      width: "10%",
      render: (text, record, index) => {
        return index + 1;
      },
    },
    {
      title: "Diet Name",
      dataIndex: "name",
      width: 220,
      editable: true,
      sorter: (a, b) => a?.name.localeCompare(b?.name),
      render: (text) => (
        <span className="diet-menu-name-cell" title={text}>
          {text}
        </span>
      ),
    },
    {
      title: "Protein",
      dataIndex: "protien",
      width: 100,
      sorter: (a, b) => a?.protien - b?.protien,
    },
    {
      title: "Calories",
      dataIndex: "calories",
      width: 100,
      editable: true,
      sorter: (a, b) => a?.calories - b?.calories,
    },
    {
      title: "Sodium",
      dataIndex: "sodium",
      width: 100,
      editable: true,
      sorter: (a, b) => a?.calories - b?.calories,
    },
    {
      title: "Potassium",
      dataIndex: "potassium",
      width: 110,
      editable: true,
      sorter: (a, b) => a?.calories - b?.calories,
    },
    {
      title: "Carbohydrates",
      dataIndex: "carbohydrates",
      width: 130,
      editable: true,
      sorter: (a, b) => a?.calories - b?.calories,
    },
    ...(defaultRole === "Facility_Admin"
      ? []
      : [
          {
            title: "Action",
            dataIndex: "operation",
            render: (_, record) => {
              const editable = isEditing(record);
              return editable ? (
                <span>
                  <Typography.Link
                    onClick={() => save(record)}
                    style={{
                      marginRight: 8,
                    }}
                  >
                    Save
                  </Typography.Link>
                  <Typography.Link
                    onClick={cancel}
                    style={{
                      marginRight: 8,
                    }}
                  >
                    Cancel
                  </Typography.Link>
                </span>
              ) : (
                <Typography.Link
                  disabled={editingKey !== ""}
                  onClick={() => edit(record)}
                >
                  Edit
                </Typography.Link>
              );
            },
          },
        ]),
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === "name" ? "text" : "number",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  const tableScrollConfig = {
    ...tableScroll,
    x: tableScroll?.y ? tableScroll.x : 880,
  };

  return (
    <>
      <Form form={form} component={false}>
        <div ref={tableWrapRef} className="diet-menu-table-wrap">
          <Table
            className="no-vertical-borders diet-menu-table"
            loading={loading}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            bordered={false}
            dataSource={data?.data}
            columns={mergedColumns}
            rowClassName="editable-row"
            pagination={false}
            scroll={tableScrollConfig}
          />
        </div>
      </Form>
      {data?.total_count > 100 && (
        <div className="flex-center" style={{ paddingTop: "10px" }}>
          <Pagination
            current={page}
            pageSize={100}
            total={data?.total_count}
            onChange={(e) => setPage(e)}
            showSizeChanger={false}
          />
        </div>
      )}
    </>
  );
};
export default DietTableEdit;
